import qs from 'qs'
import ApiService from 'api-client/ApiService'
import store from 'app/store'
import config from 'app-config'
import { sessionStoreKey } from 'core/session/sessionReducers'
import DataKeys from 'k8s/DataKeys'
import { safelyMapItems, trackApiMethodMetadata } from './helpers'
import { DexAuthToken } from './keystone.model'
import { cleanResource } from './utils'

interface CustomResourceEndpoint {
  apiVersion: string
  kind: string
}

// Define the subset of DataKeys that have CRD endpoints
type CRDDataKeys =
  | DataKeys.KaapiClusters
  | DataKeys.KaapiClusterUpgradeJobs
  | DataKeys.KaapiHostedControlPlanes
  | DataKeys.KaapiMachineDeployments
  | DataKeys.KaapiMachines
  | DataKeys.KaapiOpenStackMachineTemplates
  | DataKeys.KaapiOpenStackServers
  | DataKeys.KaapiKubeadmConfigTemplates
  | DataKeys.KaapiClusterAddons
  | DataKeys.KaapiBYOClusters
  | DataKeys.KaapiBYOHosts
  | DataKeys.KaapiBYOMachines
  | DataKeys.KaapiBYOMachineTemplates
  | DataKeys.KaapiK8sInstallerConfigTemplates
  | DataKeys.KaapiKubeadmControlPlanes

export const CRD_ENDPOINTS: Record<CRDDataKeys, CustomResourceEndpoint> = {
  [DataKeys.KaapiClusters]: {
    apiVersion: 'cluster.x-k8s.io/v1beta1',
    kind: 'clusters',
  },
  [DataKeys.KaapiClusterUpgradeJobs]: {
    apiVersion: 'core.barista.platform9.io/v1alpha1',
    kind: 'clusterupgradejobs',
  },
  [DataKeys.KaapiHostedControlPlanes]: {
    apiVersion: 'controlplane.platform9.io/v1alpha1',
    kind: 'hostedcontrolplanes',
  },
  [DataKeys.KaapiMachineDeployments]: {
    apiVersion: 'cluster.x-k8s.io/v1beta1',
    kind: 'machinedeployments',
  },
  [DataKeys.KaapiMachines]: {
    apiVersion: 'cluster.x-k8s.io/v1beta1',
    kind: 'machines',
  },
  [DataKeys.KaapiOpenStackMachineTemplates]: {
    apiVersion: 'infrastructure.cluster.x-k8s.io/v1beta1',
    kind: 'openstackmachinetemplates',
  },
  [DataKeys.KaapiOpenStackServers]: {
    apiVersion: 'infrastructure.cluster.x-k8s.io/v1alpha1',
    kind: 'openstackservers',
  },
  [DataKeys.KaapiKubeadmConfigTemplates]: {
    apiVersion: 'bootstrap.cluster.x-k8s.io/v1beta1',
    kind: 'kubeadmconfigtemplates',
  },
  [DataKeys.KaapiClusterAddons]: {
    apiVersion: 'core.barista.platform9.io/v1alpha1',
    kind: 'clusteraddons',
  },
  [DataKeys.KaapiBYOHosts]: {
    apiVersion: 'infrastructure.cluster.x-k8s.io/v1beta1',
    kind: 'byohosts',
  },
  [DataKeys.KaapiBYOMachines]: {
    apiVersion: 'infrastructure.cluster.x-k8s.io/v1beta1',
    kind: 'byomachines',
  },
  [DataKeys.KaapiBYOClusters]: {
    apiVersion: 'infrastructure.cluster.x-k8s.io/v1beta1',
    kind: 'byoclusters',
  },
  [DataKeys.KaapiBYOMachineTemplates]: {
    apiVersion: 'infrastructure.cluster.x-k8s.io/v1beta1',
    kind: 'byomachinetemplates',
  },
  [DataKeys.KaapiK8sInstallerConfigTemplates]: {
    apiVersion: 'infrastructure.cluster.x-k8s.io/v1beta1',
    kind: 'k8sinstallerconfigtemplates',
  },
  [DataKeys.KaapiKubeadmControlPlanes]: {
    apiVersion: 'controlplane.cluster.x-k8s.io/v1beta1',
    kind: 'kubeadmcontrolplanes',
  },
}

class Kubernetes extends ApiService {
  public getClassName() {
    return 'kubernetes'
  }

  static apiMethodsMetadata = []

  protected async getEndpoint() {
    return Promise.resolve(config.apiHost)
  }

  get baseUrl() {
    const state = store.getState()
    const { activeTenant } = state[sessionStoreKey]
    const { session } = state
    const { activeRegion } = session
    return `/oidc-proxy/${activeTenant}/${activeRegion}`
  }

  get kaapiNamespace() {
    const state = store.getState()
    const { activeKaapiTenant } = state[sessionStoreKey]
    return activeKaapiTenant
  }

  /**
   * Common methods for creating, deleting, getting, and patching CRDs
   * @param type CRDDataKeys - type of the CRD
   * @param name string - name of the resource
   * @returns CRD
   */
  getCRDEndpoint(type: CRDDataKeys) {
    return `${this.baseUrl}/apis/${CRD_ENDPOINTS[type].apiVersion}/namespaces/${this.kaapiNamespace}/${CRD_ENDPOINTS[type].kind}`
  }

  // Create Resource
  createCustomResource = async ({ type, body }: { type: CRDDataKeys; body: any }) => {
    const url = this.getCRDEndpoint(type)
    return this.client.basicPost<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: `create${type}`,
        useJWTToken: true,
      },
    })
  }

  // Delete Resource
  deleteCustomResource = async ({ type, name }: { type: CRDDataKeys; name: string }) => {
    const url = `${this.getCRDEndpoint(type)}/${name}`
    const data = await this.client.basicDelete<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: `delete${type}`,
        useJWTToken: true,
      },
    })
    return cleanResource()(data)
  }

  // Get Resource
  getCustomResource = async ({ type, name }: { type: CRDDataKeys; name?: string }) => {
    const url = `${this.getCRDEndpoint(type)}/${name}`
    const data = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: `get${type}`,
        useJWTToken: true,
      },
    })
    return cleanResource()(data)
  }

  // Get All Resources
  getAllCustomResources = async (type: CRDDataKeys) => {
    const url = this.getCRDEndpoint(type)
    const data = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: `getAll${type}`,
        useJWTToken: true,
      },
    })
    return safelyMapItems(data, cleanResource())
  }

  // Patch Resource
  patchCustomResource = async ({
    type,
    name,
    body,
    contentType = 'application/json-patch+json',
  }: {
    type: CRDDataKeys
    name: string
    body: any
    contentType?: string //'application/json-patch+json' | 'application/merge-patch+json'
  }) => {
    const url = `${this.getCRDEndpoint(type)}/${name}`
    const data = await this.client.basicPatch<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: `patch${type}`,
        useJWTToken: true,
        config: {
          headers: {
            'Content-Type': contentType,
          },
        },
      },
    })
    return cleanResource()(data)
  }
  // End of common methods for CRDs

  // Get clusters from all regions
  getClustersFromAllRegions = async () => {
    const url = `${this.baseUrl}/apis/cluster.x-k8s.io/v1beta1/namespaces/${this.kaapiNamespace}/clusters/all-clusters-in-pf9-tenant-namespace`
    try {
      const data = await this.client.basicGet<any>({
        url,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getClustersFromAllRegions',
          useJWTToken: true,
        },
      })
      return safelyMapItems(data, cleanResource())
    } catch (e) {
      console.log(e)
      return []
    }
  }

  // Tenant Resources
  @trackApiMethodMetadata({
    url: '/apis/core.barista.platform9.io/v1alpha1/tenants',
    type: 'GET',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'Tenant' },
  })
  getAllTenants = async () => {
    const url = `${this.baseUrl}/apis/core.barista.platform9.io/v1alpha1/tenants`
    try {
      const data = await this.client.basicGet<any>({
        url,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getTanants',
          useJWTToken: true,
        },
      })
      return cleanResource()(data)
    } catch (e) {
      console.log(e)
      return null
    }
  }

  @trackApiMethodMetadata({
    url: '/apis/core.barista.platform9.io/v1alpha1/tenants',
    type: 'GET',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'Tenant' },
  })
  // TODO: mthdName 'getTanants' below is a typo and duplicates getAllTenants' — telemetry naming only
  getKaapiTenant = async ({ tenantName }) => {
    const url = `${this.baseUrl}/apis/core.barista.platform9.io/v1alpha1/tenants/${tenantName}`
    try {
      const data = await this.client.basicGet<any>({
        endpoint: config.kaapiURL,
        url,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getTanants',
          useJWTToken: true,
        },
      })
      return cleanResource()(data)
    } catch (e) {
      console.log(e)
      return null
    }
  }

  @trackApiMethodMetadata({
    url: '/apis/core.barista.platform9.io/v1alpha1/tenants',
    type: 'POST',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'Tenant' },
  })
  createTenant = async ({ body }) => {
    const url = `${this.baseUrl}/apis/core.barista.platform9.io/v1alpha1/tenants`
    try {
      const data = await this.client.basicPost<any>({
        endpoint: config.kaapiURL,
        url,
        body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'createTenant',
          useJWTToken: true,
        },
      })
      return cleanResource()(data)
    } catch (e) {
      console.log(e)
      return null
    }
  }

  @trackApiMethodMetadata({
    url: '/resmgr/v2/dexclients',
    type: 'GET',
  })
  getDexClientSecret = async () => {
    const url = '/resmgr/v2/dexclients'
    try {
      const data = await this.client.basicGet<any>({
        url,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getDexClientSecret',
        },
      })
      return data?.kubernetes ?? ''
    } catch (e) {
      console.log(e)
      return ''
    }
  }

  @trackApiMethodMetadata({
    url: '/dex/token',
    type: 'POST',
    params: ['username', 'password', 'domain'],
    error: { isClusterError: true },
  })
  // This is to authenticate the user with the dex server and get the JWT token
  peformDexLogin = async ({
    username,
    password,
    domain = 'default',
    dex_client_secret = null,
  }): Promise<DexAuthToken | null> => {
    try {
      if (!dex_client_secret) {
        return null
      }
      const response = await this.client.rawPost<DexAuthToken>({
        url: '/dex/token',
        data: qs.stringify({
          grant_type: 'password',
          client_id: 'kubernetes',
          client_secret: dex_client_secret,
          scope: 'openid offline_access groups email profile',
          username,
          password,
          connector_id: domain,
        }),
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
        options: {
          clsName: this.getClassName(),
          mthdName: 'performDexLoin',
        },
      })
      return response.data
    } catch (err) {
      console.log(err)
      return null
    }
  }

  @trackApiMethodMetadata({
    url: '/api/v1/namespaces/:namespace/secrets/:secretname',
    type: 'GET',
    params: ['secretName'],
    error: { isClusterError: true, k8sResource: 'Secret' },
  })
  getSecret = async ({ secretName }) => {
    try {
      const response = await this.client.basicGet<any>({
        url: `${this.baseUrl}/api/v1/namespaces/${this.kaapiNamespace}/secrets/${secretName}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getSecret',
          useJWTToken: true,
        },
      })

      return response
    } catch (e) {
      console.log(e)
      return null
    }
  }

  @trackApiMethodMetadata({
    url: '/api/v1/namespaces/:namespace/secrets',
    type: 'POST',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'Secret' },
  })
  getKaapiSecrets = async ({ namespace }) => {
    try {
      const data = await this.client.basicGet<any>({
        url: `${this.baseUrl}/api/v1/namespaces/${this.kaapiNamespace}/secrets`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getKaapiSecrets',
          useJWTToken: true,
        },
      })
      return safelyMapItems(data, cleanResource())
    } catch (e) {
      console.log(e)
      return []
    }
  }

  // Events on kaapi-namespace CRs (e.g. OpenStackCluster) never surface as a
  // status condition - CAPO only ever emits a K8s Event for them - so this is
  // the only way to see cluster-infra failures like security-group quota.
  @trackApiMethodMetadata({
    url: '/api/v1/namespaces/:namespace/events',
    type: 'GET',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'Event' },
  })
  getKaapiEvents = async () => {
    try {
      const data = await this.client.basicGet<any>({
        url: `${this.baseUrl}/api/v1/namespaces/${this.kaapiNamespace}/events`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getKaapiEvents',
          useJWTToken: true,
        },
      })
      return safelyMapItems(data, cleanResource())
    } catch (e) {
      console.log(e)
      return []
    }
  }

  @trackApiMethodMetadata({
    url: '/api/v1/namespaces/:namespace/secrets',
    type: 'POST',
    params: ['namespace', 'body'],
    error: { isClusterError: true, k8sResource: 'Secret' },
  })
  createKaapiSecret = async ({ body }) => {
    try {
      const response = await this.client.basicPost<any>({
        url: `${this.baseUrl}/api/v1/namespaces/${this.kaapiNamespace}/secrets`,
        body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'createSecret',
          useJWTToken: true,
        },
      })
      return response
    } catch (e) {
      console.log(e)
      return null
    }
  }

  @trackApiMethodMetadata({
    url: '/dex/token',
    type: 'DELETE',
    params: ['namespace', 'name'],
    error: { isClusterError: true, k8sResource: 'Secret' },
  })
  deleteKaapiSecret = async ({ name }) => {
    try {
      const response = await this.client.basicDelete<any>({
        url: `${this.baseUrl}/api/v1/namespaces/${this.kaapiNamespace}/secrets/${name}`,
        options: {
          clsName: this.getClassName(),
          mthdName: 'createSecret',
          useJWTToken: true,
        },
      })
      return response
    } catch (e) {
      console.log(e)
      return null
    }
  }

  downloadKubeconfig = async ({ clusterName }) => {
    return await this.client.basicGet<any>({
      url: `${this.baseUrl}/api/v1/namespaces/${this.kaapiNamespace}/secrets/${clusterName}-oidc-kubeconfig`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'downloadKubeconfig',
        useJWTToken: true,
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/k8sapi/api/v1/namespaces/:namespace/configmaps`/',
    type: 'POST',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'ConfigMaps' },
  })
  getConfigMaps = async ({ namespace }) => {
    const url = `${this.baseUrl}/api/v1/namespaces/${this.kaapiNamespace}/configmaps`
    const data = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getConfigMaps',
        useJWTToken: true,
      },
    })
    return safelyMapItems(data, cleanResource())
  }

  @trackApiMethodMetadata({
    url: '/apis/infrastructure.cluster.x-k8s.io/v1beta1/namespaces/:namespace/byohosts',
    type: 'GET',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'BYOHost' },
  })
  getBYOHosts = async ({ namespace }) => {
    const url = `${this.baseUrl}/apis/infrastructure.cluster.x-k8s.io/v1beta1/namespaces/${this.kaapiNamespace}/byohosts`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getBYOHosts',
        useJWTToken: true,
      },
    })
    return safelyMapItems(response, cleanResource())
  }

  @trackApiMethodMetadata({
    url: '/apis/infrastructure.cluster.x-k8s.io/v1beta1/namespaces/:namespace/byomachines',
    type: 'GET',
    params: ['namespace'],
    error: { isClusterError: true, k8sResource: 'BYOMachine' },
  })
  getBYOMachines = async ({ namespace }) => {
    const url = `${this.baseUrl}/apis/infrastructure.cluster.x-k8s.io/v1beta1/namespaces/${this.kaapiNamespace}/byomachines`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getBYOMachines',
        useJWTToken: true,
      },
    })
    return safelyMapItems(response, cleanResource())
  }
}

export default Kubernetes
